import React, { FC, useEffect } from "react";
import { Box, Grid, Hidden, Link, makeStyles, Typography } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { IntermediarioLogo } from "./IntermediarioLogo";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IntermediarioActionType, loadIntermediario } from "../store/intermediario/intermediarioReducer";
import { intermediarioSelector } from "../store/intermediario/intermediario.selectors";
import { SimpleSnackbar } from "./SimpleSnackbar";
import { getProdotto } from "../store/prodotto/prodottoReducer";
import { prodottoSelector } from "../store/prodotto/prodotto.selectors";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 150,
    paddingTop: 58,
    paddingBottom: 41,
    [theme.breakpoints.down("sm")]: {
      marginTop: 31,
      marginBottom: 26,
      padding: 0,
    },
  },
  logo: {
    width: 170,
    [theme.breakpoints.down("sm")]: {
      marginBottom: 26,
    },
  },
  name: {
    fontWeight: "bold",
    marginBottom: "10px",
  },
}));

export const IntermediarioDetails: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { intermediario: id } = useParams<{ intermediario: string }>();
  const intermediarioState = useSelector(intermediarioSelector);
  const prodottoState = useSelector(prodottoSelector);
  const { error, loading, data: intermediario } = intermediarioState;
  const { loading: loadingProdotto, data: prodotto } = prodottoState;
  useEffect(() => {
    id && !intermediario && dispatch(loadIntermediario(id, history));
    id && !prodotto && dispatch(getProdotto(id));
  }, [id, intermediario, dispatch, history, prodotto]);

  if (loading || loadingProdotto) {
    return (
      <Grid container style={{ height: 50 }} className={classes.root}>
        <Hidden xsDown>
          <Grid item xs={1} />
          <Grid item xs={11}>
            <CircularProgress />
          </Grid>
        </Hidden>
      </Grid>
    );
  } else if (!intermediario) {
    if (error) {
      const handleCloseError = (): void => {
        dispatch({ type: IntermediarioActionType.CLEAR_ERROR });
      };
      return (
        <Grid container style={{ height: 50 }}>
          <SimpleSnackbar message={error} open={!!error} handleClose={handleCloseError} />
        </Grid>
      );
    } else {
      return <Grid container style={{ height: 50 }} />;
    }
  } else {
    return (
      <Grid container className={classes.root} alignItems={"flex-start"}>
        <Grid item xs={12} sm={4} md={3}>
          <Box marginRight={"40px"}>
            <IntermediarioLogo />
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Box marginRight={"30px"}>
            <Typography variant={"body1"} className={classes.name} data-test="ragioneSociale">
              {intermediario.ragioneSociale}
            </Typography>
            <Typography variant={"body2"}>
              Intermediario soggetto alla vigilanza dell’IVASS
              <br />
              Iscrizione RUI IVASS <span data-test="RUI">{intermediario.RUI}</span>
              <br />
              consultabile al link{" "}
              <a
                href="https://ruipubblico.ivass.it/rui-pubblica/ng"
                target={"_blank"}
                rel="noopener noreferrer"
                color="inherit"
              >
                https://ruipubblico.ivass.it/rui-pubblica/ng
              </a>
              <br />
              <span data-test="indirizzo">{intermediario.indirizzo}</span>
              {intermediario.pec && (
                <>
                  <br />
                  PEC:<span data-test="pec">{intermediario.pec}</span>
                </>
              )}
              {intermediario.email && (
                <>
                  <br />
                  E-mail:{" "}
                  <Link href={"mailto:" + intermediario.email} data-test="email" color="inherit">
                    {intermediario.email}
                  </Link>
                </>
              )}
              {intermediario.telefono && (
                <>
                  <br />
                  Tel: <span data-test="telefono">{intermediario.telefono}</span>
                </>
              )}
              {intermediario?.piva && (
                <>
                  <br />
                  P. iva: {intermediario?.piva}
                </>
              )}
              {intermediario?.cf && (
                <>
                  <br />
                  Cod. fisc.: {intermediario?.cf}
                </>
              )}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" style={{ marginTop: 16 }}>
            Ai recapiti sopra indicati puoi avere informazioni sui punti di contatto per presentare un reclamo
            relativamente al comportamento dell’intermediario.
            <br />
            In caso di disaccordo sull’esecuzione del contratto, prima di ricorrere all’autorità giudiziaria il cliente
            può avvalersi dei sistemi alternativi per la risoluzione delle controversie previsti dalla normativa
            vigente.
          </Typography>
        </Grid>
      </Grid>
    );
  }
};
